import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getDocuments, createDocument, updateDocument, deleteDocument } from '../services/firebase';
import { getNextProductId } from '../services/salesTransactions';
import { 
  ArrowLeft, 
  Package, 
  Plus, 
  Search, 
  Edit3, 
  Trash2,
  Loader2,
  X,
  AlertCircle
} from 'lucide-react';
import { 
  Inventory as EnhancedInventory,
  Product as EnhancedProduct,
  BeatLoadout,
  LoadoutAllocation,
  Category,
  WarehousePricelist
} from '../types/inventory';
import { Category as CategoryType } from '../types';
// Zoho types removed


interface InventoryAllocation {
  id: string;
  salesmanId: string;
  salesmanName: string;
  productId: string;
  productName: string;
  allocatedQty: number;
  remainingQty: number;
  allocatedDate: any;
  warehouseUserId: string;
}

interface Product {
  id: string;
  name?: string;
  productId?: string;
  categoryId?: string;
  categoryName?: string;
  category?: string; // Legacy field for compatibility
  unit?: string;
  isActive?: boolean;
}

interface InventoryProps {
  onBack: () => void;
}

const Inventory: React.FC<InventoryProps> = ({ onBack }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    productId: '',
    categoryId: '',
    categoryName: '',
    unit: 'pieces'
  });

  // Error handling states
  const [error, setError] = useState<string | null>(null);
  const [operationLoading, setOperationLoading] = useState<{
    add: boolean;
    update: boolean;
    delete: string | null; // productId being deleted
    refresh: boolean;
  }>({
    add: false,
    update: false,
    delete: null,
    refresh: false
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [retryCount, setRetryCount] = useState(0);
  const [lastErrorTime, setLastErrorTime] = useState<number | null>(null);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await getDocuments('categories');
      const activeCategories = categoriesData.filter((cat: CategoryType) => cat.isActive !== false);
      setCategories(activeCategories as CategoryType[]);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadProducts = async (isRetry: boolean = false) => {
    try {
      if (isRetry) {
        setOperationLoading(prev => ({ ...prev, refresh: true }));
      } else {
        setLoading(true);
      }
      
      setError(null);
      
      const productsData = await getDocuments('products');
      setProducts(productsData as Product[]);
      setRetryCount(0);
      
    } catch (error: any) {
      console.error('Error loading products:', error);
      const errorMessage = error?.message || 'Failed to load products. Please check your connection.';
      
      setError(errorMessage);
      setLastErrorTime(Date.now());
      
      // Implement exponential backoff for retries
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          loadProducts(true);
        }, delay);
      }
    } finally {
      setLoading(false);
      setOperationLoading(prev => ({ ...prev, refresh: false }));
    }
  };

  const validateFormData = () => {
    const errors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Product name must be at least 2 characters';
    }

    // Only validate other fields when adding new products (not editing)
    if (!editingProduct) {
      if (!formData.categoryId.trim()) {
        errors.category = 'Category is required';
      }
      
      if (!formData.unit.trim()) {
        errors.unit = 'Unit is required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateFormData()) {
      return;
    }

    setOperationLoading(prev => ({ ...prev, add: true }));
    setError(null);

    try {
      // Generate auto-increment productId
      const productId = await getNextProductId();
      
      const productData = {
        name: formData.name.trim(),
        productId: productId,
        categoryId: formData.categoryId,
        categoryName: formData.categoryName,
        unit: formData.unit || 'pieces',
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: 'system', // TODO: Use actual user
        updatedBy: 'system'  // TODO: Use actual user
      };

      await createDocument('products', productData);
      
      // Reset form and close modal
      setFormData({
        name: '',
        productId: '',
        categoryId: '',
        categoryName: '',
        unit: 'pieces'
      });
      setFormErrors({});
      setShowAddForm(false);
      
      // Reload products
      await loadProducts();
      
      // Show success feedback
      setTimeout(() => {
        alert('Product added successfully!');
      }, 100);

    } catch (error: any) {
      console.error('Error adding product:', error);
      const errorMessage = error?.message || 'Failed to add product. Please try again.';
      setError(`Add Product Error: ${errorMessage}`);
    } finally {
      setOperationLoading(prev => ({ ...prev, add: false }));
    }
  };

  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct) return;

    if (!validateFormData()) {
      return;
    }

    setOperationLoading(prev => ({ ...prev, update: true }));
    setError(null);

    try {
      const productData = {
        name: formData.name.trim(),
        updatedAt: serverTimestamp()
      };

      await updateDocument('products', editingProduct.id, productData);
      
      // Reset form and close modal
      setEditingProduct(null);
      setFormData({
        name: '',
        productId: '',
        categoryId: '',
        categoryName: '',
        unit: 'pieces'
      });
      setFormErrors({});
      setShowAddForm(false);
      
      // Reload products
      await loadProducts();
      
      // Show success feedback
      setTimeout(() => {
        alert('Product updated successfully!');
      }, 100);

    } catch (error: any) {
      console.error('Error updating product:', error);
      const errorMessage = error?.message || 'Failed to update product. Please try again.';
      setError(`Update Product Error: ${errorMessage}`);
    } finally {
      setOperationLoading(prev => ({ ...prev, update: false }));
    }
  };

  const handleDeleteProduct = async (id: string, productName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${productName}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    setOperationLoading(prev => ({ ...prev, delete: id }));
    setError(null);

    try {
      await deleteDocument('products', id);
      
      // Reload products
      await loadProducts();
      
      // Show success feedback
      setTimeout(() => {
        alert('Product deleted successfully!');
      }, 100);

    } catch (error: any) {
      console.error('Error deleting product:', error);
      const errorMessage = error?.message || 'Failed to delete product. Please try again.';
      setError(`Delete Product Error: ${errorMessage}`);
    } finally {
      setOperationLoading(prev => ({ ...prev, delete: null }));
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      productId: product.productId || product.id || '',
      categoryId: product.categoryId || '',
      categoryName: product.categoryName || product.category || '',
      unit: product.unit || 'pieces'
    });
    setShowAddForm(true);
  };

  const filteredProducts = products.filter(product =>
    (product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.categoryName || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-[50vh] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page header / toolbar */}
      <div className="px-6 pt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-2">
            <Package className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-semibold text-gray-800">Products</h1>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:flex-initial md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
              {operationLoading.refresh && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                </div>
              )}
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Add Product</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              {retryCount > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Retry attempt {retryCount}/3...
                </p>
              )}
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={() => {
                    setError(null);
                    loadProducts(true);
                  }}
                  className="text-xs bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200 transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={() => setError(null)}
                  className="text-xs text-red-600 hover:text-red-800 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="px-6 pb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No products found</p>
              <p className="text-gray-400 text-sm">Add your first product to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredProducts.map((product) => {
                    return (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{product.name || 'Unnamed Product'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{product.productId || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{categories.find(cat => cat.categoryId === product.categoryId)?.name || product.category || 'Uncategorized'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{product.unit || 'pieces'}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id, product.name || 'Unnamed Product')}
                              className="px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                              disabled={operationLoading.delete === product.id}
                            >
                              {operationLoading.delete === product.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Product Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-lg font-semibold mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            
            <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}>
              <div className="space-y-4">
                {/* Editable Product Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({...formData, name: e.target.value});
                      if (formErrors.name) {
                        setFormErrors(prev => ({...prev, name: ''}));
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>
                  )}
                </div>

                {/* Read-only fields when editing */}
                {editingProduct && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product ID
                      </label>
                      <input
                        type="text"
                        value={formData.productId}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category ID
                        </label>
                        <input
                          type="text"
                          value={formData.categoryId}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category Name
                        </label>
                        <input
                          type="text"
                          value={formData.categoryName}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={formData.unit}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    </div>
                  </>
                )}

                {/* Add Product form - simplified to match Edit mode */}
                {!editingProduct && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        required
                        value={formData.categoryId}
                        onChange={(e) => {
                          const selectedCategory = categories.find(cat => cat.categoryId === e.target.value);
                          setFormData({
                            ...formData, 
                            categoryId: e.target.value,
                            categoryName: selectedCategory?.name || ''
                          });
                          if (formErrors.category) {
                            setFormErrors(prev => ({...prev, category: ''}));
                          }
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.category ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select a category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.categoryId}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      {formErrors.category && (
                        <p className="text-xs text-red-600 mt-1">{formErrors.category}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <select
                        value={formData.unit}
                        onChange={(e) => setFormData({...formData, unit: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="pieces">Pieces</option>
                        <option value="kg">Kg</option>
                        <option value="liters">Liters</option>
                        <option value="boxes">Boxes</option>
                      </select>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Product ID will be auto-generated (FG001, FG002, etc.). 
                        Stock and pricing will be managed through Tally integration.
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    setFormData({
                      name: '',
                      productId: '',
                      categoryId: '',
                      categoryName: '',
                      unit: 'pieces'
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={operationLoading.add || operationLoading.update}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {(operationLoading.add || operationLoading.update) && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  <span>
                    {operationLoading.add || operationLoading.update 
                      ? (editingProduct ? 'Updating...' : 'Adding...') 
                      : (editingProduct ? 'Update' : 'Add Product')
                    }
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
